/**
 * 辩手作战台（新战术学院风 HUD）。
 *
 * 布局原则（SPEC §5）：扫视优先 + 全键盘盲操。
 * - 顶部：单行环节条（16 环节进度已整合进全局光学轨道 TopBar）；
 * - 主区：双表 Chess Clock、己方底牌、双盲揭示摘要、发言模拟台；
 * - 侧栏：断言告警、对方阵营、实时数据、演示控制、热键速查。
 *
 * 本页只消费 ArenaProvider（state + actions），不接触网络 / WebRTC。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  oppositeSide,
  sideLabel,
  type DealtCard,
  type Side,
} from '@zongheng/core-schema';
import { TopBar } from '../app/TopBar.tsx';
import { useArena } from '../state/ArenaContext.tsx';
import type { ConnectionStatus, IdentityInput } from '../state/types.ts';
import { Button } from '../components/ui/primitives.tsx';
import { AlertBar } from '../components/arena-hud/AlertBar.tsx';
import { CardPanel } from '../components/arena-hud/CardPanel.tsx';
import { ChessClock } from '../components/arena-hud/ChessClock.tsx';
import { DemoControls, type DemoAction } from '../components/arena-hud/DemoControls.tsx';
import { HotkeyLegend } from '../components/arena-hud/HotkeyLegend.tsx';
import { LiveTelemetry } from '../components/arena-hud/LiveTelemetry.tsx';
import { OpponentPanel } from '../components/arena-hud/OpponentPanel.tsx';
import { RevealPanel } from '../components/arena-hud/RevealPanel.tsx';
import { SearchOverlay } from '../components/arena-hud/SearchOverlay.tsx';
import { StageHeader } from '../components/arena-hud/StageHeader.tsx';
import { SituationStrip } from '../components/arena-hud/SituationStrip.tsx';
import { StickyNotesDock } from '../components/arena-hud/StickyNotesDock.tsx';
import { VideoWall } from '../components/system/VideoWall.tsx';
import { MediaDock } from '../components/system/MediaDock.tsx';
import { useArenaHotkeys } from '../components/arena-hud/useArenaHotkeys.ts';
import { TranscriptPanel } from '../components/arena-hud/TranscriptPanel.tsx';
import { isDemoSession, startDemoMatch } from '../components/common/demoBootstrap.ts';

const CONNECTION_LABEL: Record<ConnectionStatus, string> = {
  idle: '未连接房间',
  connecting: '正在连接…',
  connected: 'P2P 已连接',
  reconnecting: '重连中…',
  disconnected: '连接已断开',
  error: '连接异常',
};

export default function DebaterHudPage() {
  const { state, actions } = useArena();
  const selfSide: Side = state.self?.side ?? 'AFF';
  const opponentSide = oppositeSide(selfSide);
  const stage = state.stage;

  const [trapOpen, setTrapOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  /** 本地发言草稿：不完全依赖后端回写，保证 ASR/手动输入在任何后端实现下都能键入 */
  const [draft, setDraft] = useState('');

  useEffect(() => {
    // Phase 2 流式 ASR 会通过后端写入 transcriptDraft，这里同步到本地草稿
    setDraft((current) =>
      state.transcriptDraft && state.transcriptDraft !== current ? state.transcriptDraft : current,
    );
  }, [state.transcriptDraft]);

  /**
   * 己方视角下可见的底牌：沙盘只取己方视角；其余卡片只保留当前环节的牌，
   * 避免上一环节的残牌挡住「本环节无牌」状态（hud-no-card）。
   */
  const myCards = useMemo<DealtCard[]>(
    () =>
      state.dealtCards.filter((card) => {
        if (card.kind === 'SANDBOX') return card.perspective === selfSide;
        if (state.stageIndex > 0 && card.card.stageIndex !== state.stageIndex) return false;
        return true;
      }),
    [state.dealtCards, state.stageIndex, selfSide],
  );
  const currentCard = myCards[0] ?? null;
  const currentCardKey = currentCard
    ? `${currentCard.kind}:${
        currentCard.kind === 'SANDBOX'
          ? `${currentCard.card.cardId}:${currentCard.perspective}`
          : currentCard.card.cardId
      }`
    : null;

  /** 切换底牌时收起锦囊，避免把上一张卡的战术带到下一环节 */
  useEffect(() => {
    setTrapOpen(false);
  }, [currentCardKey]);

  /** 当前底牌上可勾选的必打论据 id 列表（Digit 1/2/3 与 UI 勾选共用） */
  const evidenceIds = useMemo<string[]>(() => {
    if (!currentCard) return [];
    if (currentCard.kind === 'CONSTRUCTIVE') {
      return currentCard.card.mandatoryEvidences.map((evidence) => evidence.id);
    }
    if (currentCard.kind === 'SANDBOX') {
      return currentCard.card.perspectives[currentCard.perspective].mustHitEvidenceIds;
    }
    return [];
  }, [currentCard]);

  const opponent =
    state.participants.find(
      (participant) => participant.role === 'DEBATER' && participant.side === opponentSide,
    ) ?? null;

  const oppReveal = stage?.pairId
    ? (state.reveals.find(
        (reveal) => reveal.pairId === stage.pairId && reveal.side === opponentSide,
      ) ?? null)
    : null;

  const ownAlerts = useMemo(
    () => state.alerts.filter((alert) => alert.side === selfSide),
    [state.alerts, selfSide],
  );

  const canToggle =
    state.phase === 'RUNNING' && state.stagePhase === 'SPEAKING' && Boolean(stage?.allowToggle);

  const toggleSpeaker = () => {
    if (!canToggle) return;
    actions.toggleSpeaker(state.runningSide === 'AFF' ? 'NEG' : 'AFF');
  };

  /* ------------------------------ 全局热键 ------------------------------ */
  useArenaHotkeys({
    searchOpen: state.search.open,
    toggleSpeaker,
    toggleCounterTrap: () => setTrapOpen((open) => !open),
    toggleEvidence: (index: number) => {
      const evidenceId = evidenceIds[index];
      if (evidenceId) actions.toggleEvidence(selfSide, evidenceId);
    },
    openSearch: () => actions.openSearch(state.search.query),
    closeSearch: () => actions.closeSearch(),
  });

  /* ------------------------------ 演示控制 ------------------------------ */
  const isDemo = isDemoSession(state);
  const fallbackIdentity: IdentityInput = {
    name: state.self?.name ?? '演示辩手',
    role: 'DEBATER',
    side: selfSide,
    seat: state.self?.seat ?? 1,
  };

  const handleDemoAction = async (action: DemoAction) => {
    if (action === 'start') {
      setBusy(true);
      try {
        await startDemoMatch(actions, fallbackIdentity, state.roomId ?? undefined);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (action === 'advance') actions.advanceStage();
    if (action === 'pause') actions.pauseMatch();
    if (action === 'resume') actions.resumeMatch();
    if (action === 'reveal') actions.forceReveal();
    if (action === 'reset') {
      actions.resetMatch();
      // 演示后端重置后可能把路由切回教练台，作战台保持在场以便继续演示。
      actions.setRoute('arena');
    }
  };

  /* -------------------------------- 视图 -------------------------------- */
  return (
    <div data-testid="hud-root" className="min-h-screen">
      <TopBar compact />
      <main className="mx-auto max-w-[1600px] space-y-3 px-3 py-3 sm:px-4">
        <StageHeader
          stage={stage}
          stageIndex={state.stageIndex}
          stagePhase={state.stagePhase}
          phase={state.phase}
          self={state.self}
          selfSide={selfSide}
          connectionLabel={CONNECTION_LABEL[state.connection]}
        />

        <SituationStrip />

        <VideoWall prefix="hud" compact />

        {state.phase === 'FINISHED' && (
          <div
            data-testid="hud-finished"
            className="lens flex flex-wrap items-center justify-between gap-3 px-5 py-4"
          >
            <div>
              <p className="text-sm font-semibold text-slate-100">训练已结束 · 16 环节完成</p>
              <p className="text-[11px] text-slate-400">可前往复盘台查看双盲揭示验证与战报。</p>
            </div>
            <Button size="sm" variant="success" onClick={() => actions.setRoute('review')}>
              进入复盘台
            </Button>
          </div>
        )}

        <div data-testid="hud-workspace" className="workspace-shell">
          <div className="space-y-3">
            <ChessClock
              stage={stage}
              remainingMs={state.remainingMs}
              runningSide={state.runningSide}
              stagePhase={state.stagePhase}
              prepRemainingMs={state.prepRemainingMs}
              phase={state.phase}
              selfSide={selfSide}
              canToggle={canToggle}
              onToggleSpeaker={toggleSpeaker}
            />

            <CardPanel
              card={currentCard}
              selfSide={selfSide}
              checkedEvidenceIds={state.manualEvidenceChecks[selfSide] ?? []}
              trapOpen={trapOpen}
              onToggleTrap={() => setTrapOpen((open) => !open)}
              onToggleEvidence={(evidenceId) => actions.toggleEvidence(selfSide, evidenceId)}
            />
          </div>

          <aside className="workspace-rail space-y-3">
            <RevealPanel
              reveals={state.reveals}
              pairId={stage?.pairId ?? null}
              stageIndex={state.stageIndex}
              selfSide={selfSide}
            />
            <OpponentPanel
              opponent={opponent}
              opponentSide={opponentSide}
              revealed={oppReveal}
            />
            <AlertBar alerts={ownAlerts} />
            <LiveTelemetry
              selfSide={selfSide}
              evidenceIds={evidenceIds}
              manualEvidenceChecks={state.manualEvidenceChecks}
              peering={state.peering}
              vadMode={state.vadMode}
              speechStats={state.speechStats}
            />
            <TranscriptPanel
              selfSide={selfSide}
              draft={draft}
              onChangeDraft={(text) => {
                setDraft(text);
                actions.setTranscriptDraft(text);
              }}
              onSubmit={() => {
                actions.submitTranscript(selfSide, draft);
                setDraft('');
                actions.setTranscriptDraft('');
              }}
            />
            <MediaDock prefix="hud" />
            <DemoControls
              isDemo={isDemo}
              phase={state.phase}
              stageIndex={state.stageIndex}
              busy={busy}
              onAction={handleDemoAction}
            />
            <HotkeyLegend />
          </aside>
        </div>
      </main>

      <SearchOverlay
        search={state.search}
        selfSideLabel={sideLabel(selfSide)}
        onChangeQuery={(query) => actions.setSearchQuery(query)}
        onMoveSelection={(delta) => actions.moveSearchSelection(delta)}
        onClose={() => actions.closeSearch()}
      />
      <StickyNotesDock />
    </div>
  );
}
